import React, { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { api, useSEO, getRecentlyViewed } from "@/lib/api";
import { MaskReveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const COLLECTIONS = [
  { v: "all", l: "All" },
  { v: "new-arrivals", l: "New Arrivals" },
  { v: "signature-collection", l: "Signature Collection" },
];
const PRICES = [
  { v: "all", l: "All prices" },
  { v: "0-1500", l: "Under ₹1,500" },
  { v: "1500-1800", l: "₹1,500 – ₹1,800" },
  { v: "1800-999999", l: "₹1,800+" },
];

export default function Shop() {
  useSEO("Shop All", "Shop the complete VYRON collection — luxury polarized eyewear crafted for everyday luxury.");
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [collection, setCollection] = useState("all");
  const [price, setPrice] = useState("all");
  const [sort, setSort] = useState("featured");
  const [mobileFilters, setMobileFilters] = useState(false);
  const recent = getRecentlyViewed();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let arr = [...products];
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter((p) => p.name.toLowerCase().includes(q) || p.tagline.toLowerCase().includes(q) || p.collections.join(" ").includes(q));
    }
    if (collection !== "all") arr = arr.filter((p) => p.collections.includes(collection));
    if (price !== "all") {
      const [lo, hi] = price.split("-").map(Number);
      arr = arr.filter((p) => p.price >= lo && p.price <= hi);
    }
    if (sort === "price-low") arr.sort((a, b) => a.price - b.price);
    else if (sort === "price-high") arr.sort((a, b) => b.price - a.price);
    else if (sort === "newest") arr.sort((a, b) => Number(b.new_arrival) - Number(a.new_arrival));
    return arr;
  }, [products, query, collection, price, sort]);

  const clearAll = () => { setQuery(""); setCollection("all"); setPrice("all"); };

  const Filters = () => (
    <div className="space-y-8" data-testid="shop-filters">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Collection</p>
        <div className="flex flex-wrap gap-2">
          {COLLECTIONS.map((c) => (
            <button key={c.v} onClick={() => setCollection(c.v)} data-testid={`filter-collection-${c.v}`}
              className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${collection === c.v ? "bg-black text-white border-black" : "border-black/15 hover:border-black"}`}>{c.l}</button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Price</p>
        <div className="flex flex-col gap-2">
          {PRICES.map((p) => (
            <button key={p.v} onClick={() => setPrice(p.v)} data-testid={`filter-price-${p.v}`}
              className={`text-xs uppercase tracking-widest px-4 py-2.5 rounded-full border text-left transition-colors ${price === p.v ? "bg-black text-white border-black" : "border-black/15 hover:border-black"}`}>{p.l}</button>
          ))}
        </div>
      </div>
      <button onClick={clearAll} className="text-xs uppercase tracking-widest link-underline" data-testid="clear-filters">Clear all</button>
    </div>
  );

  return (
    <div data-testid="shop-page" className="pt-32 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="mb-10">
          <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.9]"><MaskReveal>Shop All</MaskReveal></h1>
        </div>

        {/* Search + sort */}
        <div className="flex flex-col md:flex-row gap-3 mb-10">
          <div className="flex-1 relative">
            <Search size={17} className="absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="shop-search" placeholder="Search frames…" value={query} onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-[#F5F5F5] rounded-full pl-12 pr-5 py-4 text-sm outline-none focus:ring-1 ring-black transition" />
          </div>
          <button onClick={() => setMobileFilters(true)} data-testid="mobile-filters-btn"
            className="md:hidden inline-flex items-center justify-center gap-2 border border-black/15 rounded-full px-6 py-4 text-xs uppercase tracking-widest">
            <SlidersHorizontal size={15} /> Filters
          </button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-full md:w-[200px] rounded-full border-black/15 py-4 h-auto" data-testid="shop-sort"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-10">
          <aside className="hidden md:block sticky top-24 self-start">
            <Filters />
          </aside>
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-6">{filtered.length} results</p>
            {filtered.length === 0 ? (
              <p className="text-muted-foreground" data-testid="shop-empty">No frames match your filters.</p>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="shop-grid">
                {filtered.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>

        {/* Recently viewed */}
        {recent.length > 0 && (
          <section className="mt-24" data-testid="recently-viewed">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-8">Recently viewed</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {recent.slice(0, 4).map((p, i) => <ProductCard key={p.slug} product={{ ...p, images: [p.image] }} index={i} />)}
            </div>
          </section>
        )}
      </div>

      {/* Mobile filters drawer */}
      {mobileFilters && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-filters-drawer">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="font-display text-2xl">Filters</p>
              <button onClick={() => setMobileFilters(false)}><X size={22} /></button>
            </div>
            <Filters />
            <button onClick={() => setMobileFilters(false)} className="w-full mt-8 bg-black text-white rounded-full py-4 uppercase text-xs tracking-[0.2em]">Show {filtered.length} results</button>
          </div>
        </div>
      )}
    </div>
  );
}
